import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useHasAccessToNorfolkSouthern from '@rsos/hooks/useHasAccessToNorfolkSouthern';
import useHasAccessToTowPro from '@rsos/hooks/useHasAccessToTowPro';
import {
  digitalDispatchApi,
  DIGITAL_DISPATCH_EVENT_TYPES,
} from '@rsos/reduxDataStore/src/features/resourceRequests/digitalDispatchApi';
import useStreamWsReconnectTask from '@rsos/reduxDataStore/src/utils/useStreamWsReconnectTask';
import type { ReconnectTaskStreamWS } from '@rsos/reduxDataStore/src/utils/useStreamWsReconnectTask';

const DIGITAL_DISPATCH_EVENTS = new Set<string>(
  Object.values(DIGITAL_DISPATCH_EVENT_TYPES),
);

/**
 * Subscribes to StreamWS and invalidates the digital dispatch RTK Query cache
 * when digital dispatch websocket events arrive, so tow and consist request
 * rows update without waiting for the next poll. Matches other
 * streamWS_hooks patterns.
 */
const useHandleDigitalDispatchMessages = (): void => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const hasAccessToTowPro = useHasAccessToTowPro();
  const hasAccessToNorfolkSouthern = useHasAccessToNorfolkSouthern();
  const enabled = hasAccessToTowPro || hasAccessToNorfolkSouthern;

  const invalidateRequests = useCallback(() => {
    dispatch(
      digitalDispatchApi.util.invalidateTags(['DigitalDispatchRequests']),
    );
  }, [dispatch]);

  const handleDigitalDispatchMessage = useCallback(
    (message: Record<string, unknown>) => {
      if (DIGITAL_DISPATCH_EVENTS.has(message?.type as string)) {
        invalidateRequests();
      }
    },
    [invalidateRequests],
  );

  const refetchOnReconnect = useCallback(
    () => Promise.resolve(invalidateRequests()),
    [invalidateRequests],
  );

  useStreamWsReconnectTask({
    streamWS: StreamWS as ReconnectTaskStreamWS | undefined,
    enabled,
    run: refetchOnReconnect,
    callbackName: 'handleDigitalDispatchReconnect',
    sentryTag: 'digital-dispatch-reconnect-refetch',
  });

  useEffect(() => {
    if (!StreamWS || !enabled) {
      return;
    }

    StreamWS.addOnMessageCallback({
      handleDigitalDispatchMessage,
    });

    return () => {
      StreamWS.removeOnMessageCallback('handleDigitalDispatchMessage');
    };
  }, [StreamWS, handleDigitalDispatchMessage, enabled]);
};

export default useHandleDigitalDispatchMessages;
